import { useEffect, useRef, useState } from 'react'
import { gsap, SplitText, useGSAP, prefersReducedMotion } from '../lib/gsap'
import { useLang } from '../lib/useLang'
import { STACK, UI } from '../data/content'
import { stackIcons } from '../data/svgIcons'
import Logo from '../components/Logo'

// Fibonacci-distributed icons on a sphere that auto-rotates, follows drag with inertia, and orbits the logo.
function TagSphere({ highlight, onHover }) {
  const root = useRef(null)
  const items = useRef([])

  useEffect(() => {
    const el = root.current
    const n = STACK.length
    const points = STACK.map((_, i) => {
      const phi = Math.acos(-1 + (2 * i + 1) / n)
      const theta = Math.sqrt(n * Math.PI) * phi
      return { x: Math.cos(theta) * Math.sin(phi), y: Math.sin(theta) * Math.sin(phi), z: Math.cos(phi) }
    })
    let R = el.offsetWidth * 0.4
    let rx = 0.3
    let ry = 0
    let vx = 0.0015
    let vy = 0.004
    let dragging = false
    let last = { x: 0, y: 0 }
    let visible = true
    const auto = prefersReducedMotion() ? 0 : 1

    const ro = new ResizeObserver(() => (R = el.offsetWidth * 0.4))
    ro.observe(el)
    const io = new IntersectionObserver(([e]) => (visible = e.isIntersecting))
    io.observe(el)

    const tick = () => {
      if (!visible) return
      if (!dragging) {
        vx += (0.0012 * auto - vx) * 0.02
        vy += (0.0035 * auto - vy) * 0.02
      }
      rx += vx
      ry += vy
      const cx = Math.cos(rx)
      const sx = Math.sin(rx)
      const cy = Math.cos(ry)
      const sy = Math.sin(ry)
      points.forEach((p, i) => {
        const node = items.current[i]
        if (!node) return
        // rotate around Y then X
        const x1 = p.x * cy + p.z * sy
        const z1 = -p.x * sy + p.z * cy
        const y2 = p.y * cx - z1 * sx
        const z2 = p.y * sx + z1 * cx
        const depth = (z2 + 1) / 2 // 0 back → 1 front
        const scale = 0.55 + depth * 0.65
        node.style.transform = `translate3d(${x1 * R}px, ${y2 * R}px, 0) translate(-50%, -50%) scale(${scale})`
        node.style.opacity = String(0.25 + depth * 0.75)
        node.style.zIndex = String(Math.round(depth * 100))
        node.style.filter = depth < 0.35 ? `blur(${(0.35 - depth) * 6}px)` : 'none'
      })
    }
    gsap.ticker.add(tick)

    const down = (e) => {
      dragging = true
      last = { x: e.clientX, y: e.clientY }
      el.setPointerCapture(e.pointerId)
    }
    const move = (e) => {
      if (!dragging) return
      vy = (e.clientX - last.x) * 0.004
      vx = -(e.clientY - last.y) * 0.004
      last = { x: e.clientX, y: e.clientY }
    }
    const up = () => (dragging = false)
    el.addEventListener('pointerdown', down)
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerup', up)
    el.addEventListener('pointercancel', up)
    return () => {
      gsap.ticker.remove(tick)
      ro.disconnect()
      io.disconnect()
      el.removeEventListener('pointerdown', down)
      el.removeEventListener('pointermove', move)
      el.removeEventListener('pointerup', up)
      el.removeEventListener('pointercancel', up)
    }
  }, [])

  return (
    <div ref={root} data-cursor="drag" data-cursor-label="Drag" className="relative aspect-square w-full max-w-[600px] touch-pan-y select-none">
      <div className="absolute inset-[4%] rounded-full opacity-60 blur-[60px]" style={{ background: 'radial-gradient(circle, #2D3190 0%, rgba(45,49,144,0.55) 40%, transparent 70%)' }} />
      <div className="absolute inset-[8%] rounded-full border border-white/[0.06]" />
      <div className="absolute inset-[22%] rounded-full border border-dashed border-white/[0.06]" />
      <div className="absolute left-1/2 top-1/2 z-[50] -translate-x-1/2 -translate-y-1/2">
        <Logo className="h-20 w-20 drop-shadow-[0_0_40px_rgba(45,92,255,0.8)] sm:h-24 sm:w-24" />
      </div>
      {STACK.map((key, i) => (
        <div key={key} ref={(n) => (items.current[i] = n)} className="absolute left-1/2 top-1/2 will-change-transform">
          <div
            onPointerEnter={() => onHover(key)}
            onPointerLeave={() => onHover(null)}
            className={`glass grid h-16 w-16 place-items-center rounded-2xl p-3.5 transition-[box-shadow,transform] duration-500 sm:h-[76px] sm:w-[76px] sm:p-4 [&_svg]:h-full [&_svg]:w-full ${
              highlight === key ? 'scale-125 shadow-[0_0_40px_rgba(45,92,255,0.9)]' : ''
            }`}
            style={{ borderRadius: 20 }}
          >
            <div>{stackIcons[key]?.svg}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default function Stack() {
  const root = useRef(null)
  const { t, lang } = useLang()
  const [hl, setHl] = useState(null)

  useGSAP(
    () => {
      const split = SplitText.create('[data-stack-title]', { type: 'words,chars', mask: 'chars' })
      gsap.from(split.chars, { yPercent: 110, stagger: 0.03, scrollTrigger: { trigger: '[data-stack-title]', start: 'top 85%' } })
      gsap.from('[data-tool]', { opacity: 0, x: -20, stagger: 0.03, duration: 0.8, scrollTrigger: { trigger: '[data-tools]', start: 'top 85%' } })
      gsap.from('[data-sphere]', { scale: 0.6, opacity: 0, rotate: -30, duration: 1.8, scrollTrigger: { trigger: '[data-sphere]', start: 'top 85%' } })
      return () => split.revert()
    },
    { scope: root, dependencies: [lang], revertOnUpdate: true },
  )

  return (
    <section id="stack" ref={root} className="relative overflow-hidden py-28 sm:py-40">
      <div className="relative mx-auto grid max-w-[1440px] items-center gap-16 px-5 sm:px-10 lg:grid-cols-2">
        <div className="flex flex-col gap-8">
          <span className="label text-mist">(04) — {t(UI.stackSub)}</span>
          <h2 key={lang} data-stack-title className="font-display text-[clamp(3rem,6vw,6rem)] font-semibold leading-[0.88] tracking-[-0.045em] text-white">
            {t(UI.stackTitle)}
          </h2>
          <ul data-tools className="grid max-w-[520px] grid-cols-2 gap-x-8 sm:grid-cols-3">
            {STACK.map((key) => (
              <li
                key={key}
                data-tool
                onPointerEnter={() => setHl(key)}
                onPointerLeave={() => setHl(null)}
                className={`flex items-center gap-2 border-b border-white/[0.06] py-2.5 text-sm transition-colors duration-300 ${hl === key ? 'text-white' : 'text-mist'}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full transition-all duration-300 ${hl === key ? 'scale-150 bg-royal shadow-[0_0_10px_#2D5CFF]' : 'bg-white/20'}`} />
                {stackIcons[key]?.name}
              </li>
            ))}
          </ul>
        </div>
        <div data-sphere className="flex justify-center">
          <TagSphere highlight={hl} onHover={setHl} />
        </div>
      </div>
    </section>
  )
}
